import type { JSX, ChangeEvent, KeyboardEvent } from 'react';
import { useContext, useEffect, useRef } from 'react';
import { styled } from 'styled-components';
import { isNotNil } from '@wistia/type-guards';
import type { InputProps } from '../Input';
import { Input } from '../Input';
import { EditableTextContext } from './EditableTextRoot';
import type { TypographicVariant } from '../../private/helpers/getTypographicStyles/getTypographicStyles';
import { getTypographicStyles } from '../../private/helpers/getTypographicStyles/getTypographicStyles';

const StyledInput = styled(Input)<{
  $typographicVariant: TypographicVariant;
  $minLines: number;
  $maxLines: number | 'infinity';
}>`
  && {
    ${({ $minLines }) =>
      isNotNil($minLines) &&
      `min-height: calc(${$minLines}lh + calc(var(--wui-editable-text-padding) * 2));`}
    ${({ $maxLines }) =>
      isNotNil($maxLines) &&
      `max-height: calc(${$maxLines}lh + calc(var(--wui-editable-text-padding) * 2));`}
    ${({ $typographicVariant }) => getTypographicStyles($typographicVariant)}
    background-color: var(--wui-color-bg-surface);
    border-radius: var(--wui-editable-text-border-radius);
    padding: var(--wui-editable-text-padding);
    resize: none;
  }
`;

export type EditableTextInputProps = Pick<InputProps, 'autoSelect'>;

export const EditableTextInput = (props: EditableTextInputProps): JSX.Element | null => {
  const context = useContext(EditableTextContext);
  if (!context) {
    throw new Error('EditableTextInput must be used within an EditableTextRoot context');
  }
  const {
    isEditing,
    value,
    setValue,
    onValueCommit,
    setIsEditing,
    typographicVariant,
    submitMode,
    originalValue,
    onValueRevert,
    id,
    placeholder,
    minLines,
    maxLines,
    finalFocusEl,
  } = context;

  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      if (isEditing) {
        const element = inputRef.current;
        const { style } = element;
        style.height = '0px';
        const { scrollHeight } = element;
        style.height = `${scrollHeight}px`;
      }
      if (isEditing) {
        inputRef.current.focus();
      }
    }
  }, [value, isEditing]);

  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setValue(event.target.value);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (
      (submitMode === 'enter' || submitMode === 'both') &&
      event.key === 'Enter' &&
      !event.shiftKey
    ) {
      event.preventDefault();
      onValueCommit?.(value);
      setIsEditing(false);
      setTimeout(() => {
        const element = finalFocusEl?.();
        if (element) {
          element.focus();
        }
      }, 0);
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      setValue(originalValue);
      onValueRevert?.(originalValue);
      setIsEditing(false);
      setTimeout(() => {
        const element = finalFocusEl?.();
        if (element) {
          element.focus();
        }
      }, 0);
    }
  };

  const handleBlur = () => {
    if (submitMode === 'blur' || submitMode === 'both') {
      onValueCommit?.(value);
      setIsEditing(false);
      setTimeout(() => {
        const element = finalFocusEl?.();
        if (element) {
          element.focus();
        }
      }, 0);
    }
  };

  if (!isEditing) {
    return null;
  }

  return (
    <StyledInput
      ref={inputRef}
      $maxLines={maxLines ?? 'infinity'}
      $minLines={minLines}
      $typographicVariant={typographicVariant}
      data-wui-editable-text-input={true}
      id={id}
      onBlur={handleBlur}
      onChange={handleChange}
      onFocus={(event) => {
        const { length } = event.currentTarget.value;

        // place caret at end of input on focus
        event.currentTarget.setSelectionRange(length, length);
      }}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      type="multiline"
      value={value}
      {...props}
    />
  );
};
